import { NoticeBlock } from "@/components/NoticeBlock.tsx";
import { CurrencyPromo } from "@/components/l10n/Currency.tsx";
import { useConfig } from "@/config.ts";
import { TopUpNowButton } from "@/modules/billing/ActionButtons.tsx";
import { useBillingAccount } from "@/modules/billing/billingAccountQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { queryAtom } from "@/modules/billing/restrictionLevelClearTopupThresholdQuery.ts";
import { getTransText } from "@/modules/l10n/getTransText.ts";
import { MB } from "@/modules/l10n/units.tsx";
import { ContactSupportButton } from "@/modules/support/ContactSupportButton.tsx";
import { useThemeProps } from "@/modules/theme/useTheme.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { BillingAccount } from "@warrenio/api-spec/spec.oats.gen";
import { filterFalse } from "@warrenio/utils/collections/filterFalse";
import type { ReactNode } from "react";

export function LimitedBillingAccountNoticeLoader({ baId }: { baId: BillingAccount["id"] }) {
    const account = useBillingAccount(baId);
    return <LimitedBillingAccountNoticeContent account={account} />;
}

export function LimitsList() {
    const { t } = useLingui();
    const {
        limitedBillingAccountConfig: { ram_mb, vcpu },
        kubernetesEnabled,
        vmsEnabled,
        bareMetalEnabled,
    } = useConfig();

    const limits = filterFalse<ReactNode>([
        vmsEnabled && !!vcpu && (
            <>
                <Trans>vCPU</Trans>: {vcpu}
            </>
        ),
        vmsEnabled && !!ram_mb && (
            <>
                <Trans>RAM</Trans>: {ram_mb} {MB}
            </>
        ),
        bareMetalEnabled && t`1 bare metal server`,
        bareMetalEnabled && t`Hourly billing cycle only`,
        kubernetesEnabled && t`Kubernetes service can not be created.`,
    ]);

    if (limits.length === 0) {
        return null;
    }

    return (
        <ul className="m-0 pl-5">
            {limits.map((limit, idx) => (
                <li key={idx}>{limit}</li>
            ))}
        </ul>
    );
}

export function LimitedBillingAccountNoticeContent({ account }: { account: EBillingAccount }) {
    //#region Hooks
    const { restriction_level_clear_topup_threshold } = useSuspenseQueryAtom(queryAtom);
    const { limitedBillingAccountHideButton, limitedBillingAccountCopy } = useThemeProps();
    const { billingEmail } = useConfig();
    const { i18n } = useLingui();
    //#endregion

    return (
        account.isLimited && (
            <NoticeBlock
                icon="jp-warning-icon"
                button={
                    !limitedBillingAccountHideButton &&
                    (account.canTopUp ? <TopUpNowButton account={account} /> : <ContactSupportButton />)
                }
            >
                <p>
                    <b className="uppercase">
                        <Trans>Billing account is limited</Trans>
                    </b>
                    <br />
                    <Trans>This billing account has limited capabilities when creating resources.</Trans>{" "}
                    {!!limitedBillingAccountCopy &&
                        getTransText(limitedBillingAccountCopy, i18n).replace("<billing_email>", billingEmail)}
                </p>

                {!limitedBillingAccountCopy && (
                    <>
                        <LimitsList />

                        {account.isPostPay ? (
                            <p>
                                <Trans>Please contact support to remove the limit.</Trans>
                            </p>
                        ) : restriction_level_clear_topup_threshold ? (
                            <p>
                                <Trans>
                                    Limit will be removed once the total top up amount reaches{" "}
                                    <CurrencyPromo value={restriction_level_clear_topup_threshold} />.
                                </Trans>
                            </p>
                        ) : null}
                    </>
                )}
            </NoticeBlock>
        )
    );
}
